import { useState, useEffect } from 'react';
import ClientRow from '../../components/journey/ClientRow/ClientRow';
import ClientHeader from '../../components/journey/ClientHeader/ClientHeader';
import StageList from '../../components/journey/StageList/StageList';
import StageDetail from '../../components/journey/StageDetail/StageDetail';
import ActivityMode from '../../components/journey/ActivityMode/ActivityMode';
import {
  getClientList,
  getClientDetail,
  toggleChecklistItem,
  submitStageForReview,
  approveStage,
  rejectStage,
  addNote
} from '../../api/clientApi';
import './ClientJourney.css';

const SEGMENTS = [
  { key: null, label: 'All' },
  { key: 'pending_review', label: 'Pending' },
  { key: 'not_started', label: 'Not Started' },
  { key: 'near_complete', label: 'Near Complete' }
];

const CURRENT_USER_ROLE = 'Administrator';
const MIN_WIDTH = 180;

export default function ClientJourney() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [selectedStageUid, setSelectedStageUid] = useState(null);
  const [activeTab, setActiveTab] = useState('journey');
  const [activeSegment, setActiveSegment] = useState(null);
  const [searchText, setSearchText] = useState('');

  const [saving, setSaving] = useState(false);
  const [gateError, setGateError] = useState(null);

  const [users, setUsers] = useState([]);

  // Resizable panel widths (px)
  const [p1Width, setP1Width] = useState(280);
  const [p2Width, setP2Width] = useState(240);
  const [p1Collapsed, setP1Collapsed] = useState(false);
  const [p2Collapsed, setP2Collapsed] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchClients();
  }, []);

  async function fetchUsers() {
    try {
      const res = await fetch('/api/users');
      const data = await res.json();
      if (data.success) setUsers(data.data || []);
    } catch (err) {}
  }

  async function fetchClients() {
    try {
      setLoading(true);
      const data = await getClientList();
      setClients(data || []);
    } catch (err) {
      setError('Could not load clients. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchClientDetail(clientId) {
    try {
      setDetailLoading(true);
      const data = await getClientDetail(clientId);
      setSelectedClient(data);
      setSelectedStageUid(null);
      setActiveTab('journey');
    } catch (err) {
      setError('Could not load client detail.');
    } finally {
      setDetailLoading(false);
    }
  }

  function handleSelectClient(client) {
    setSelectedClientId(client._id);
    setGateError(null);
    fetchClientDetail(client._id);
  }

  async function handleToggleItem(stageUid, itemUid) {
    if (!selectedClient) return;
    try {
      setSaving(true);
      const updated = await toggleChecklistItem(selectedClient._id, stageUid, itemUid, 'current-user');
      setSelectedClient(updated);
      updateClientInList(updated);
    } catch (err) {
      setGateError('Could not update checklist item.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmitComplete(stageUid) {
    if (!selectedClient) return;
    try {
      setSaving(true);
      setGateError(null);
      const updated = await submitStageForReview(selectedClient._id, stageUid, 'current-user');
      setSelectedClient(updated);
      updateClientInList(updated);
    } catch (err) {
      const msg = err.message || '';
      if (msg.toLowerCase().includes('gate')) {
        setGateError('Cannot mark complete: a required preceding stage is not yet complete.');
      } else {
        setGateError(msg || 'Could not submit stage for review.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove(stageUid) {
    if (!selectedClient) return;
    try {
      setSaving(true);
      const updated = await approveStage(selectedClient._id, stageUid, 'current-user', '');
      setSelectedClient(updated);
      updateClientInList(updated);
    } catch (err) {
      setGateError('Could not approve stage.');
    } finally {
      setSaving(false);
    }
  }

  async function handleReject(stageUid, comment) {
    if (!selectedClient) return;
    try {
      setSaving(true);
      const updated = await rejectStage(selectedClient._id, stageUid, 'current-user', comment);
      setSelectedClient(updated);
      updateClientInList(updated);
    } catch (err) {
      setGateError('Could not reject stage.');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddNote(noteType, rawText) {
    if (!selectedClient) return;
    try {
      setSaving(true);
      await addNote(selectedClient._id, noteType, rawText, 'current-user');
      const updated = await getClientDetail(selectedClient._id);
      setSelectedClient(updated);
    } catch (err) {
      setGateError('Could not save note.');
    } finally {
      setSaving(false);
    }
  }

  function updateClientInList(updatedClient) {
    setClients(prev => {
      const newList = [];
      for (let i = 0; i < prev.length; i++) {
        if (prev[i]._id === updatedClient._id) {
          newList.push({
            ...prev[i],
            overallPercent: updatedClient.overallPercent,
            stageSummaries: updatedClient.stageSummaries,
            hasPendingReview: updatedClient.hasPendingReview
          });
        } else {
          newList.push(prev[i]);
        }
      }
      return newList;
    });
  }

  function getFilteredClients() {
    let filtered = [...clients];
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      filtered = filtered.filter(c => {
        const name = (c.name || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        const phone = (c.phone || '').toLowerCase();
        return name.includes(lower) || email.includes(lower) || phone.includes(lower);
      });
    }
    if (activeSegment === 'pending_review') {
      filtered = filtered.filter(c => c.hasPendingReview);
    } else if (activeSegment === 'not_started') {
      filtered = filtered.filter(c => (c.overallPercent || 0) === 0);
    } else if (activeSegment === 'near_complete') {
      filtered = filtered.filter(c => (c.overallPercent || 0) >= 80);
    }
    return filtered;
  }

  function handleSegmentClick(key) {
    setActiveSegment(activeSegment === key ? null : key);
  }

  function startDrag(e, which) {
    e.preventDefault();
    const startX = e.clientX;
    const startW1 = p1Width;
    const startW2 = p2Width;

    function move(ev) {
      const delta = ev.clientX - startX;
      if (which === '12') {
        setP1Width(Math.max(MIN_WIDTH, Math.min(480, startW1 + delta)));
      } else {
        setP2Width(Math.max(MIN_WIDTH, Math.min(420, startW2 + delta)));
      }
    }

    function end() {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', end);
    }

    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', end);
  }

  const filteredClients = getFilteredClients();

  const selectedStage = selectedClient && selectedStageUid
    ? (selectedClient.stageDetails || []).find(s => s.stageUid === selectedStageUid)
    : null;

  if (loading) return <div className="loading-state">Loading clients...</div>;
  if (error && !selectedClient) return <div className="error-state">{error}</div>;

  return (
    <div className="cj">

      {/* ── PANEL 1: Client list ── */}
      {!p1Collapsed && (
        <div className="cj__p1" style={{ width: p1Width }}>
          <div className="cj__p1-header">
            <h1 className="cj__page-title">Clients</h1>
            <input
              className="cj__search"
              type="text"
              placeholder="Search name, email, phone..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            <div className="cj__segments">
              {SEGMENTS.map(seg => (
                <button
                  key={seg.key || 'all'}
                  className={`cj__seg-btn ${activeSegment === seg.key ? 'cj__seg-btn--active' : ''}`}
                  onClick={() => handleSegmentClick(seg.key)}
                >
                  {seg.label}
                </button>
              ))}
            </div>
          </div>
          <div className="cj__p1-body">
            {filteredClients.length === 0 && (
              <p className="cj__list-empty">No clients match your search.</p>
            )}
            {filteredClients.map(client => (
              <ClientRow
                key={client._id}
                client={client}
                isSelected={selectedClientId === client._id}
                onClick={() => handleSelectClient(client)}
                users={users}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── DIVIDER 1 ── */}
      <div className="cj__divider">
        {!p1Collapsed && (
          <div className="cj__divider-drag" onMouseDown={e => startDrag(e, '12')} />
        )}
        <button
          className="cj__divider-btn"
          onClick={() => setP1Collapsed(!p1Collapsed)}
          title={p1Collapsed ? 'Show clients' : 'Hide clients'}
        >
          {p1Collapsed ? '▶' : '◀'}
        </button>
      </div>

      {/* ── RIGHT AREA ── */}
      <div className="cj__right">
        {!selectedClient && !detailLoading && (
          <div className="cj__empty-state">
            <div className="cj__empty-icon">🗺️</div>
            <p className="cj__empty-title">Select a client</p>
            <p className="cj__empty-sub">Click any client on the left to view their journey.</p>
          </div>
        )}

        {detailLoading && <div className="loading-state">Loading journey...</div>}

        {selectedClient && !detailLoading && (
          <div className="cj__right-inner">
            <ClientHeader client={selectedClient} users={users} />

            <div className="cj__tabs-row">
              <button
                className={`cj__tab ${activeTab === 'journey' ? 'cj__tab--active' : ''}`}
                onClick={() => setActiveTab('journey')}
              >
                Journey
              </button>
              <button
                className={`cj__tab ${activeTab === 'activity' ? 'cj__tab--active' : ''}`}
                onClick={() => setActiveTab('activity')}
              >
                Activity
              </button>
            </div>

            <div className="cj__tab-body">
              {activeTab === 'journey' && (
                <div className="cj__journey-panels">

                  {/* ── PANEL 2: Stage list ── */}
                  {!p2Collapsed && (
                    <div className="cj__p2" style={{ width: p2Width }}>
                      <StageList
                        stageDetails={selectedClient.stageDetails || []}
                        selectedStageUid={selectedStageUid}
                        onSelectStage={setSelectedStageUid}
                      />
                    </div>
                  )}

                  {/* ── DIVIDER 2 ── */}
                  <div className="cj__divider">
                    {!p2Collapsed && (
                      <div className="cj__divider-drag" onMouseDown={e => startDrag(e, '23')} />
                    )}
                    <button
                      className="cj__divider-btn"
                      onClick={() => setP2Collapsed(!p2Collapsed)}
                      title={p2Collapsed ? 'Show stages' : 'Hide stages'}
                    >
                      {p2Collapsed ? '▶' : '◀'}
                    </button>
                  </div>

                  {/* ── PANEL 3: Stage detail + notes ── */}
                  <div className="cj__p3">
                    {!selectedStage ? (
                      <div className="cj__p3-empty">
                        <span className="cj__p3-empty-icon">📋</span>
                        <p className="cj__p3-empty-title">Select a stage</p>
                        <p className="cj__p3-empty-sub">Click a stage to view its checklist and notes.</p>
                      </div>
                    ) : (
                      <StageDetail
                        stage={selectedStage}
                        onToggleItem={handleToggleItem}
                        onSubmitComplete={handleSubmitComplete}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onAddNote={handleAddNote}
                        notes={selectedClient.notes || []}
                        currentUserRole={CURRENT_USER_ROLE}
                        saving={saving}
                        gateError={gateError}
                        onClearGateError={() => setGateError(null)}
                        users={users}
                      />
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'activity' && (
                <div className="cj__activity-wrap">
                  <ActivityMode
                    activityLog={selectedClient.activityLog || []}
                    users={users}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
